'use strict';

/**
 * Lightweight Product Loader - Replaces int-vue-productpool
 * 
 * Matches exact HTML structure from the Vue library including:
 * - VueCarousel image carousel with multiple images
 * - Price structure with pricetype-b, valuewrap, price-reg, price-sale
 * - Ratings structure
 * - Color swatches container
 */

var IMAGE_BASE_URL = 'https://slimages.macysassets.com/is/image/MCY/products/';
var IMAGE_PARAMS = '?op_sharpen=1&layer=1&bgcolor=100,100,100&opac=5&alt=61';
var SWATCH_PARAMS = '?fmt=jpeg&wid=30';

/**
 * Extract products from Macy's xAPI response
 */
function extractProducts(data) {
    if (!data) {
        console.log('[ProductLoader] extractProducts: no data');
        return [];
    }

    console.log('[ProductLoader] extractProducts: data keys:', Object.keys(data));

    // Macy's xAPI structure: body.canvas.rows[].rowSortableGrid.zones[].sortableGrid.collection[]
    if (data.body && data.body.canvas && data.body.canvas.rows) {
        var products = [];
        var rows = data.body.canvas.rows;
        console.log('[ProductLoader] Found rows:', rows.length);

        for (var i = 0; i < rows.length; i++) {
            var row = rows[i];
            if (row.rowSortableGrid && row.rowSortableGrid.zones) {
                var zones = row.rowSortableGrid.zones;
                console.log('[ProductLoader] Row', i, 'has', zones.length, 'zones');
                
                for (var j = 0; j < zones.length; j++) {
                    var zone = zones[j];
                    console.log('[ProductLoader] Zone', j, 'keys:', Object.keys(zone));
                    
                    // Products are in zone.sortableGrid.collection (NOT zone.collection)
                    if (zone.sortableGrid && zone.sortableGrid.collection && zone.sortableGrid.collection.length > 0) {
                        var collection = zone.sortableGrid.collection;
                        console.log('[ProductLoader] Found sortableGrid.collection with', collection.length, 'items');
                        
                        for (var k = 0; k < collection.length; k++) {
                            var item = collection[k];
                            // Products are wrapped in {product: {...}}
                            if (item.product) {
                                products.push(item.product);
                            }
                        }
                    }
                }
            }
        }

        if (products.length > 0) {
            console.log('[ProductLoader] Extracted', products.length, 'products');
            return products;
        }
    }

    // Fallback checks for other structures
    if (Array.isArray(data)) {
        // Check if this is an array of {product: {...}} objects (from /xapi/discover/v1/product endpoint)
        if (data.length > 0 && data[0].product) {
            var unwrapped = [];
            for (var i = 0; i < data.length; i++) {
                if (data[i].product) {
                    unwrapped.push(data[i].product);
                }
            }
            return unwrapped;
        }
        return data;
    }
    if (data.products) return data.products;
    if (data.items) return data.items;
    if (data.data && Array.isArray(data.data)) return data.data;

    console.log('[ProductLoader] No products found in any known structure');
    return [];
}

/**
 * Get all product images (for carousel)
 */
function getProductImages(product) {
    var images = [];

    // Try additionalImageSource first (has all images)
    if (product.imagery && product.imagery.additionalImageSource) {
        var additional = product.imagery.additionalImageSource;
        for (var i = 0; i < additional.length; i++) {
            if (additional[i].filePath) {
                images.push(IMAGE_BASE_URL + additional[i].filePath + IMAGE_PARAMS);
            }
        }
    }

    // Also check selected color imagery
    if (images.length === 0 && product.traits && product.traits.colors && product.traits.colors.selectedColor) {
        var colorImagery = product.traits.colors.selectedColor.imagery;
        if (colorImagery && colorImagery.additionalImageSource) {
            var colorAdditional = colorImagery.additionalImageSource;
            for (var j = 0; j < colorAdditional.length; j++) {
                if (colorAdditional[j].filePath) {
                    images.push(IMAGE_BASE_URL + colorAdditional[j].filePath + IMAGE_PARAMS);
                }
            }
        }
    }

    // Fallback to primary image only
    if (images.length === 0 && product.imagery && product.imagery.primaryImage && product.imagery.primaryImage.filePath) {
        images.push(IMAGE_BASE_URL + product.imagery.primaryImage.filePath + IMAGE_PARAMS);
    }

    return images;
}

/**
 * Get the colorMap array from traits.colors (all available color swatches)
 * Returns an empty array if no colorMap or only one color with no swatchImage.
 */
function getColorMap(product) {
    if (!product.traits || !product.traits.colors) return [];
    var colors = product.traits.colors;

    // colorMap is the array of all available colors
    if (colors.colorMap && colors.colorMap.length > 0) {
        return colors.colorMap;
    }

    // If there is no colorMap but there is a selectedColor with a swatchImage,
    // wrap it into a single-entry array so the swatch still renders.
    if (colors.selectedColor && colors.selectedColor.swatchImage) {
        return [colors.selectedColor];
    }

    return [];
}

/**
 * Get the images for a specific color entry from colorMap
 */
function getImagesForColor(colorEntry) {
    var images = [];
    if (colorEntry.imagery && colorEntry.imagery.additionalImageSource) {
        var additional = colorEntry.imagery.additionalImageSource;
        for (var i = 0; i < additional.length; i++) {
            if (additional[i].filePath && !additional[i].showJumboSwatch) {
                images.push(IMAGE_BASE_URL + additional[i].filePath + IMAGE_PARAMS);
            }
        }
    }
    if (images.length === 0 && colorEntry.imagery && colorEntry.imagery.primaryImage && colorEntry.imagery.primaryImage.filePath) {
        images.push(IMAGE_BASE_URL + colorEntry.imagery.primaryImage.filePath + IMAGE_PARAMS);
    }
    return images;
}

/**
 * Generate color swatches HTML matching Vue library DOM structure:
 * <div class="colors">
 *   <div class="color-swatches">
 *     <div data-first colorid="8548541" colorname="Blue" class="color-swatch"
 *          style="background-image: url(...);"></div>
 *   </div>
 * </div>
 */
function getColorSwatchesHtml(product) {
    var colorMap = getColorMap(product);
    var selectedColor = (product.traits && product.traits.colors && product.traits.colors.selectedColor) || null;
    var selectedId = selectedColor ? selectedColor.id : null;

    var html = '<div class="colors">';
    html += '<div class="color-swatches">';

    if (colorMap.length > 0) {
        for (var i = 0; i < colorMap.length; i++) {
            var color = colorMap[i];
            if (!color.swatchImage || !color.swatchImage.filePath) continue;

            var swatchUrl = IMAGE_BASE_URL + color.swatchImage.filePath + SWATCH_PARAMS;
            var isFirst = (color.id === selectedId) || (i === 0 && !selectedId);
            // Encode the image list as a data attribute so the click handler can swap images
            var colorImages = getImagesForColor(color);

            html += '<div';
            if (isFirst) html += ' data-first';
            html += ' colorid="' + color.id + '"';
            html += ' colorname="' + (color.normalName || color.name || '') + '"';
            html += ' class="color-swatch' + (isFirst ? ' active' : '') + '"';
            html += ' style="background-image: url(&quot;' + swatchUrl + '&quot;);"';
            html += ' data-images=\'' + JSON.stringify(colorImages) + '\'';
            html += '></div>';
        }
    } else {
        html += '<!---->'; // Vue comment placeholder when no swatches
    }

    html += '</div>'; // close color-swatches
    html += '</div>'; // close colors
    return html;
}

/**
 * Get product URL
 */
function getProductUrl(product) {
    if (product.identifier && product.identifier.productUrl) {
        return product.identifier.productUrl;
    }
    var id = product.id || (product.identifier && product.identifier.productId);
    return id ? '/shop/product?ID=' + id : '#';
}

/**
 * Get product name
 */
function getProductName(product) {
    if (product.detail && product.detail.name) {
        return product.detail.name;
    }
    return product.name || '';
}

/**
 * Get product brand
 */
function getProductBrand(product) {
    if (product.detail && product.detail.brand) {
        return product.detail.brand;
    }
    return product.brand || '';
}

/**
 * Get product ID
 */
function getProductId(product) {
    return product.id || (product.identifier && product.identifier.productId) || '';
}

/**
 * Get price type ID (e.g., 24)
 */
function getPriceTypeId(product) {
    if (product.pricing && product.pricing.price && product.pricing.price.priceTypeId) {
        return product.pricing.price.priceTypeId;
    }
    return 24;
}

/**
 * Generate price HTML matching Vue library structure exactly:
 * <div class="price ptype-24">
 *   <div class="pricetype-b">Limited-Time Special</div>
 *   <div class="valuewrap">
 *     <div length="2" pricetype="24" class="regular">
 *       <span class="price-reg regular"><span>$110.00</span></span>
 *     </div>
 *     <div class>
 *       <span class="price-sale">"Sale "<span>$87.99</span></span>
 *     </div>
 *   </div>
 * </div>
 */
function getPriceHtml(product) {
    var priceTypeId = getPriceTypeId(product);
    var html = '<div class="price ptype-' + priceTypeId + '">';

    // Get price type text (e.g., "Limited-Time Special")
    var priceTypeText = '';
    if (product.pricing && product.pricing.price && product.pricing.price.priceType) {
        priceTypeText = product.pricing.price.priceType.text || '';
    }

    if (priceTypeText) {
        html += '<div class="pricetype-b">' + priceTypeText + '</div>';
    }

    html += '<div class="valuewrap">';

    // Process tiered prices
    if (product.pricing && product.pricing.price && product.pricing.price.tieredPrice) {
        var tieredPrice = product.pricing.price.tieredPrice;
        var priceCount = tieredPrice.length;

        // Check if ANY tier has a discount price
        var hasDiscount = false;
        for (var i = 0; i < tieredPrice.length; i++) {
            var tierValues = tieredPrice[i].values || [];
            for (var k = 0; k < tierValues.length; k++) {
                if (tierValues[k].type === 'discount') {
                    hasDiscount = true;
                    break;
                }
            }
            if (hasDiscount) break;
        }

        for (var i = 0; i < tieredPrice.length; i++) {
            var tier = tieredPrice[i];
            var values = tier.values || [];
            var label = tier.label || '';

            // Separate regular and discount values
            var regularValues = [];
            var discountValues = [];
            for (var j = 0; j < values.length; j++) {
                var priceValue = values[j];
                var type = priceValue.type || 'regular';
                if (type === 'regular') {
                    regularValues.push(priceValue);
                } else if (type === 'discount') {
                    discountValues.push(priceValue);
                }
            }

            // Render regular prices (strikethrough if there's a discount)
            if (regularValues.length === 1) {
                var regValue = regularValues[0];
                var formattedReg = regValue.formattedValue || ('$' + regValue.value);
                html += '<div length="' + priceCount + '" pricetype="' + priceTypeId + '" class="regular">';
                html += '<span class="price-reg regular"' + (hasDiscount ? ' style="text-decoration: line-through;"' : '') + '><span>' + formattedReg + '</span></span>';
                html += '</div>';
            } else if (regularValues.length > 1) {
                // Multiple regular prices - show as range
                var regMin = regularValues[0].value;
                var regMax = regularValues[0].value;
                for (var r = 1; r < regularValues.length; r++) {
                    if (regularValues[r].value < regMin) regMin = regularValues[r].value;
                    if (regularValues[r].value > regMax) regMax = regularValues[r].value;
                }
                var formattedRegRange = '$' + regMin.toFixed(2) + ' - $' + regMax.toFixed(2);
                html += '<div length="' + priceCount + '" pricetype="' + priceTypeId + '" class="regular">';
                html += '<span class="price-reg regular"' + (hasDiscount ? ' style="text-decoration: line-through;"' : '') + '><span>' + formattedRegRange + '</span></span>';
                html += '</div>';
            }

            // Render discount prices
            if (discountValues.length === 1) {
                // Single discount price
                var discValue = discountValues[0];
                var formattedDisc = discValue.formattedValue || ('$' + discValue.value);
                var percentOff = discValue.formattedPercentOff || '';
                html += '<div class="">';
                html += '<span class="price-sale"><span>' + formattedDisc + '</span> ' + percentOff + '</span>';
                html += '</div>';
            } else if (discountValues.length > 1) {
                // Multiple discount prices - show as range
                var discMin = discountValues[0].value;
                var discMax = discountValues[0].value;

                // Collect all percentOff values to potentially show as range
                // percentOff can be an array (e.g., [40]) so we access [0]
                var percentOffValues = [];
                for (var d = 0; d < discountValues.length; d++) {
                    if (discountValues[d].value < discMin) discMin = discountValues[d].value;
                    if (discountValues[d].value > discMax) discMax = discountValues[d].value;

                    // Handle percentOff as array (per Vue library pattern)
                    var pOff = discountValues[d].percentOff;
                    if (pOff && Array.isArray(pOff) && pOff.length > 0) {
                        percentOffValues.push(pOff[0]);
                    } else if (typeof pOff === 'number') {
                        percentOffValues.push(pOff);
                    }
                }

                // Build percent off display - show as range if values differ
                var percentOffDisplay = '';
                if (percentOffValues.length > 0) {
                    var minPercent = Math.min.apply(null, percentOffValues);
                    var maxPercent = Math.max.apply(null, percentOffValues);
                    if (minPercent === maxPercent) {
                        percentOffDisplay = '(' + minPercent + '% off)';
                    } else {
                        percentOffDisplay = '(' + minPercent + '-' + maxPercent + '% off)';
                    }
                }

                var formattedDiscRange = '$' + discMin.toFixed(2) + ' - $' + discMax.toFixed(2);
                html += '<div class="">';
                html += '<span class="price-sale"><span>' + formattedDiscRange + '</span> ' + percentOffDisplay + '</span>';
                html += '</div>';
            }
        }
    }

    html += '</div>'; // close valuewrap
    html += '</div>'; // close price

    return html;
}

/**
 * Generate ratings HTML matching Vue library structure:
 * <div class="ratings"></div>
 * 
 * Note: The Vue library shows empty ratings divs most of the time
 */
function getRatingsHtml(product) {
    var html = '<div class="ratings">';

    // Check if product has rating data
    if (product.detail && product.detail.reviewStatistics && product.detail.reviewStatistics.aggregate) {
        var rating = product.detail.reviewStatistics.aggregate.rating || 0;
        var count = product.detail.reviewStatistics.aggregate.count || 0;

        if (rating > 0 && count > 0) {
            var percentage = (rating / 5) * 100;
            html += '<div class="ratings-wrapper">';
            html += '<div class="ratings-background"></div>';
            html += '<div class="ratings-fill" style="width: ' + percentage + '%;"></div>';
            html += '</div>';
            html += '<div class="ratings-count">(' + count + ')</div>';
        }
    }

    html += '</div>';
    return html;
}

/**
 * Generate image carousel HTML matching Vue library structure exactly:
 * <div class="VueCarousel">
 *   <div class="VueCarousel-wrapper">
 *     <div class="VueCarousel-inner" style="...">
 *       <div tabindex="-1" role="tabpanel" class="VueCarousel-slide VueCarousel-slide-active VueCarousel-slide-center">
 *         <img src="...">
 *       </div>
 *       <div tabindex="-1" role="tabpanel" class="VueCarousel-slide" aria-hidden="true">
 *         <img src="...">
 *       </div>
 *       ...
 *     </div>
 *   </div>
 * </div>
 */
function getCarouselHtml(images) {
    if (!images || images.length === 0) {
        return '';
    }

    var html = '<div class="VueCarousel">';
    html += '<div class="VueCarousel-wrapper">';
    html += '<div class="VueCarousel-inner" style="transform: translate(0px, 0px); transition: transform 0.25s; flex-basis: 220px; visibility: visible;">';

    for (var i = 0; i < images.length; i++) {
        var isActive = (i === 0);
        var slideClasses = 'VueCarousel-slide';

        if (isActive) {
            slideClasses += ' VueCarousel-slide-active VueCarousel-slide-center';
        }

        html += '<div tabindex="-1" role="tabpanel" class="' + slideClasses + '"';
        if (!isActive) {
            html += ' aria-hidden="true"';
        }
        html += '>';
        html += '<img src="' + images[i] + '">';
        html += '</div>';
    }

    html += '</div>'; // close VueCarousel-inner
    html += '</div>'; // close VueCarousel-wrapper
    html += '</div>'; // close VueCarousel

    return html;
}

/**
 * Create an influencer card to show at the start of the product carousel
 * @param {Object} influencer - { title, desc, image }
 */
function createInfluencerCard(influencer) {
    if (!influencer) return null;

    var card = document.createElement('div');
    card.className = 'productgrid-product influencer-card';
    if (influencer.id) {
        card.setAttribute('data-influencer-id', influencer.id);
    }

    var html = '';
    html += '<div class="influencer-content">';
    html += '<div class="influencer-text">';
    // title supports HTML
    html += '<div class="influencer-title">' + influencer.title + '</div>';
    if (influencer.desc) {
        html += '<div class="influencer-desc">' + influencer.desc + '</div>';
    }
    html += '</div>';
    if (influencer.image) {
        html += '<div class="influencer-image">';
        html += '<img src="' + influencer.image + '" alt="Influencer">';
        html += '</div>';
    }
    html += '</div>';

    card.innerHTML = html;
    return card;
}

/**
 * Create an influencer "end" card to show at the end of the product carousel.
 * Matches the same color theme as the start card via data-influencer-id.
 * The entire card is a clickable link.
 * @param {Object} influencer - { id, shopLabel, link }
 */
function createInfluencerEndCard(influencer) {
    if (!influencer) return null;

    var card = document.createElement('div');
    card.className = 'productgrid-product influencer-card influencer-end-card';
    if (influencer.id) {
        card.setAttribute('data-influencer-id', influencer.id);
    }

    var shopLabel = influencer.shopLabel || "their picks";
    var link = influencer.link || '#';

    var html = '';
    // Wrap entire card in <a> to make it fully clickable
    html += '<a href="' + link + '" class="influencer-end-link" data-linktrack="' + (influencer.linktrack || '') + '">';
    html += '<div class="influencer-content influencer-end-content">';
    html += '<div class="influencer-end-dots"><span>&bull;</span><span>&bull;</span><span>&bull;</span></div>';
    html += '<div class="influencer-end-shop">Shop ' + shopLabel + '</div>';
    html += '</div>';
    html += '</a>';

    card.innerHTML = html;
    return card;
}

/**
 * Create a product card matching Vue library structure exactly
 */
function createProductCard(product) {
    var productId = getProductId(product);
    var productUrl = getProductUrl(product);
    var productName = getProductName(product);
    var productBrand = getProductBrand(product);
    var images = getProductImages(product);

    var card = document.createElement('div');
    card.setAttribute('data-id', productId);
    card.className = 'productgrid-product';

    var html = '';

    // Product link wraps everything
    html += '<a href="' + productUrl + '&lid=prom_productgrid-' + productId + '">';

    // Product image container
    html += '<div class="productgrid-productimage">';
    html += '<!---->'; // Vue comment placeholder
    html += '<div class="productgrid-imagewrap">';
    html += '<!---->'; // Vue comment placeholder
    html += '<!---->'; // Vue comment placeholder

    // Image carousel
    html += getCarouselHtml(images);

    html += '</div>'; // close productgrid-imagewrap
    html += '</div>'; // close productgrid-productimage

    // Price type badge (above product details)
    if (product.pricing && product.pricing.price && product.pricing.price.priceType) {
        var priceTypeText = product.pricing.price.priceType.text || '';
        if (priceTypeText) {
            html += '<div class="pricetype-b">' + priceTypeText + '</div>';
        }
    }

    // Product details
    html += '<div class="productgrid-product-details">';

    // Colors/swatches
    html += getColorSwatchesHtml(product);

    // Brand
    html += '<div class="productgrid-product-details-brand">' + productBrand + '</div>';

    // Name
    html += '<div class="productgrid-product-details-name">' + productName + '</div>';

    // Price (without pricetype-b, which is now above)
    var priceHtml = getPriceHtml(product);
    var priceWithoutBadge = priceHtml.replace(/<div class="pricetype-b">.*?<\/div>\s*/i, '');
    html += priceWithoutBadge;

    // Ratings
    html += getRatingsHtml(product);

    html += '</div>'; // close productgrid-product-details

    html += '</a>';

    card.innerHTML = html;

    return card;
}

/**
 * Initialize carousel functionality on product cards
 */
function initCarousel(carouselEl) {
    if (!carouselEl) return;

    var inner = carouselEl.querySelector('.VueCarousel-inner');
    var slides = carouselEl.querySelectorAll('.VueCarousel-slide');
    if (!inner || slides.length < 2) return;

    var currentIndex = 0;
    var slideWidth = 100; // percentage

    function goToSlide(index) {
        if (index < 0) index = slides.length - 1;
        if (index >= slides.length) index = 0;

        currentIndex = index;
        inner.style.transform = 'translate(-' + (currentIndex * slideWidth) + '%, 0px)';

        for (var i = 0; i < slides.length; i++) {
            slides[i].classList.remove('VueCarousel-slide-active', 'VueCarousel-slide-center');
            slides[i].setAttribute('aria-hidden', i !== currentIndex ? 'true' : 'false');
        }
        slides[currentIndex].classList.add('VueCarousel-slide-active', 'VueCarousel-slide-center');
        slides[currentIndex].removeAttribute('aria-hidden');
    }

    var cycleInterval = null;
    var cycleDelayMs = 1000;

    function startCycling() {
        if (cycleInterval || slides.length < 2) return;
        cycleInterval = setInterval(function() {
            goToSlide(currentIndex + 1);
        }, cycleDelayMs);
    }

    function stopCycling() {
        if (cycleInterval) {
            clearInterval(cycleInterval);
            cycleInterval = null;
        }
        goToSlide(0);
    }

    // Desktop: mouse hover to cycle through images
    carouselEl.addEventListener('mouseenter', function() {
        startCycling();
    });

    carouselEl.addEventListener('mouseleave', function() {
        stopCycling();
    });

    // Mobile: press-and-hold to cycle through images
    var pressTimer = null;
    var pressActive = false;
    var suppressClick = false;
    var startX = 0;
    var startY = 0;
    var longPressDelayMs = 250;
    var moveTolerancePx = 10;

    function clearPressTimer() {
        if (pressTimer) {
            clearTimeout(pressTimer);
            pressTimer = null;
        }
    }

    function getPointFromEvent(evt) {
        if (evt && evt.touches && evt.touches.length) return evt.touches[0];
        if (evt && evt.changedTouches && evt.changedTouches.length) return evt.changedTouches[0];
        return evt;
    }

    function onPressStart(evt) {
        var point = getPointFromEvent(evt);
        if (!point) return;

        pressActive = false;
        suppressClick = false;
        startX = point.clientX || 0;
        startY = point.clientY || 0;

        clearPressTimer();
        pressTimer = setTimeout(function() {
            pressActive = true;
            suppressClick = true;
            startCycling();
        }, longPressDelayMs);
    }

    function onPressMove(evt) {
        var point = getPointFromEvent(evt);
        if (!point) return;

        var dx = Math.abs((point.clientX || 0) - startX);
        var dy = Math.abs((point.clientY || 0) - startY);
        if (dx > moveTolerancePx || dy > moveTolerancePx) {
            // User is scrolling/dragging; cancel long-press or stop cycling.
            clearPressTimer();
            if (pressActive) {
                pressActive = false;
                stopCycling();
            }
        }
    }

    function onPressEnd() {
        clearPressTimer();
        if (pressActive) {
            pressActive = false;
            stopCycling();
            // Prevent the subsequent click from following the product link.
            setTimeout(function() {
                suppressClick = false;
            }, 0);
        }
    }

    // If a long-press started cycling, suppress click navigation once.
    carouselEl.addEventListener('click', function(e) {
        if (suppressClick) {
            e.preventDefault();
            e.stopPropagation();
        }
    }, true);

    var hasPointerEvents = (typeof window !== 'undefined') && ('PointerEvent' in window);
    if (hasPointerEvents) {
        carouselEl.addEventListener('pointerdown', function(e) {
            if (e.pointerType === 'touch' || e.pointerType === 'pen') {
                onPressStart(e);
            }
        }, { passive: true });
        carouselEl.addEventListener('pointermove', function(e) {
            if (e.pointerType === 'touch' || e.pointerType === 'pen') {
                onPressMove(e);
            }
        }, { passive: true });
        carouselEl.addEventListener('pointerup', function(e) {
            if (e.pointerType === 'touch' || e.pointerType === 'pen') {
                onPressEnd();
            }
        });
        carouselEl.addEventListener('pointercancel', function(e) {
            if (e.pointerType === 'touch' || e.pointerType === 'pen') {
                onPressEnd();
            }
        });
    } else {
        // Fallback for older browsers: touch events
        carouselEl.addEventListener('touchstart', onPressStart, { passive: true });
        carouselEl.addEventListener('touchmove', onPressMove, { passive: true });
        carouselEl.addEventListener('touchend', onPressEnd);
        carouselEl.addEventListener('touchcancel', onPressEnd);
    }
}

/**
 * Initialize all carousels in a container
 */
function initCarousels(container) {
    if (!container) return;
    var carousels = container.querySelectorAll('.VueCarousel');
    for (var i = 0; i < carousels.length; i++) {
        initCarousel(carousels[i]);
    }
}

/**
 * Initialize swatch click handlers inside a container.
 * Clicking a swatch replaces the carousel images with that color's imagery.
 */
function initSwatches(container) {
    if (!container) return;
    var productCards = container.querySelectorAll('.productgrid-product');

    for (var p = 0; p < productCards.length; p++) {
        (function(card) {
            var swatches = card.querySelectorAll('.color-swatch');
            if (swatches.length < 2) return; // nothing to toggle

            for (var s = 0; s < swatches.length; s++) {
                swatches[s].addEventListener('click', function(e) {
                    e.preventDefault();
                    e.stopPropagation();

                    var swatch = e.currentTarget;

                    // Mark active
                    var siblings = card.querySelectorAll('.color-swatch');
                    for (var k = 0; k < siblings.length; k++) {
                        siblings[k].classList.remove('active');
                    }
                    swatch.classList.add('active');

                    // Parse the images for this color
                    var imagesJson = swatch.getAttribute('data-images');
                    var images;
                    try { images = JSON.parse(imagesJson); } catch (err) { images = []; }
                    if (!images || images.length === 0) return;

                    // Rebuild the carousel HTML
                    var carouselWrap = card.querySelector('.productgrid-imagewrap');
                    if (!carouselWrap) return;

                    var newCarouselHtml = getCarouselHtml(images);
                    // Keep the Vue comment placeholders then append the carousel
                    carouselWrap.innerHTML = '<!---->' + '<!---->' + newCarouselHtml;

                    // Re-init carousel behaviour for the new slides
                    var newCarousel = carouselWrap.querySelector('.VueCarousel');
                    if (newCarousel) {
                        initCarousel(newCarousel);
                    }
                });
            }
        })(productCards[p]);
    }
}

/**
 * Fetch products from xAPI
 */
function fetchProducts(poolUrl) {
    return new Promise(function(resolve, reject) {
        var url = poolUrl;

        // Ensure URL starts with /xapi for proxy
        if (url.indexOf('/xapi') !== 0 && url.indexOf('http') !== 0) {
            url = '/xapi' + url;
        }

        console.log('[ProductLoader] Fetching URL:', url);

        // Use XMLHttpRequest for better compatibility
        var xhr = new XMLHttpRequest();
        xhr.open('GET', url, true);
        xhr.setRequestHeader('Accept', 'application/json');
        
        xhr.onreadystatechange = function() {
            if (xhr.readyState === 4) {
                console.log('[ProductLoader] XHR complete, status:', xhr.status);
                if (xhr.status >= 200 && xhr.status < 300) {
                    try {
                        var data = JSON.parse(xhr.responseText);
                        console.log('[ProductLoader] Parsed data, keys:', Object.keys(data));
                        resolve(data);
                    } catch (e) {
                        console.error('[ProductLoader] JSON parse error:', e);
                        reject(e);
                    }
                } else {
                    console.error('[ProductLoader] HTTP error:', xhr.status);
                    reject(new Error('HTTP error: ' + xhr.status));
                }
            }
        };
        
        xhr.onerror = function() {
            console.error('[ProductLoader] XHR network error');
            reject(new Error('Network error'));
        };
        
        xhr.send();
    });
}

/**
 * Render products into a productgrid
 * @param {Object} options.influencer - Optional influencer data { title, desc, image }
 */
function renderProducts(productgrid, products, options) {
    options = options || {};
    var productsContainer = productgrid.querySelector('.productgrid-products');
    if (!productsContainer) return;

    // Clear existing products
    productsContainer.innerHTML = '';
    productsContainer.removeAttribute('data-placeholder');

    // Add influencer card first if provided
    if (options.influencer) {
        var influencerCard = createInfluencerCard(options.influencer);
        if (influencerCard) {
            productsContainer.appendChild(influencerCard);
        }
    }

    // Render each product
    for (var i = 0; i < products.length; i++) {
        var product = products[i];

        // Skip products with price type text (pricetype-b) such as Clearance or Last Act
        // Allow "Limited-Time Special" through
        if (true) {
            var priceTypeText = '';
            if (product.pricing && product.pricing.price && product.pricing.price.priceType) {
                priceTypeText = product.pricing.price.priceType.text || '';
            }

            if (priceTypeText && priceTypeText !== 'Limited-Time Special') {
                continue;
            }
        }

        var card = createProductCard(product);
        productsContainer.appendChild(card);
    }

    // Add influencer end card last if provided
    if (options.influencer) {
        var influencerEndCard = createInfluencerEndCard(options.influencer);
        if (influencerEndCard) {
            productsContainer.appendChild(influencerEndCard);
        }
    }

    // Initialize carousels after products are rendered
    initCarousels(productsContainer);

    // Initialize swatch click-to-swap behaviour
    initSwatches(productsContainer);
}

/**
 * Load a single productgrid
 * @param {Object} options.influencer - Optional influencer data
 */
function loadProductGrid(productgrid, options) {
    options = options || {};
    var poolUrl = productgrid.getAttribute('data-pool');
    console.log('[ProductLoader] loadProductGrid called, poolUrl:', poolUrl);
    if (!poolUrl) return Promise.resolve();

    return fetchProducts(poolUrl)
        .then(function(data) {
            console.log('[ProductLoader] Data fetched, extracting products...');
            var products = extractProducts(data);
            console.log('[ProductLoader] Extracted products count:', products.length);
            if (products.length > 0) {
                console.log('[ProductLoader] First product:', products[0]);
            }
            renderProducts(productgrid, products, options);

            // Dispatch event for skeleton loading system
            var event;
            if (typeof CustomEvent === 'function') {
                event = new CustomEvent('loadpool', { bubbles: true });
            } else {
                event = document.createEvent('CustomEvent');
                event.initCustomEvent('loadpool', true, true, {});
            }
            productgrid.dispatchEvent(event);
        })
        .catch(function(error) {
            console.error('ProductLoader: Failed to load products:', error);
        });
}

/**
 * Start - Initialize all productgrids on the page
 * Compatible with int-vue-productpool API
 */
function start() {
    console.log('[ProductLoader] start() called');
    var productgrids = document.querySelectorAll('.productgrid[data-pool]');
    console.log('[ProductLoader] Found productgrids:', productgrids.length);

    for (var i = 0; i < productgrids.length; i++) {
        console.log('[ProductLoader] Loading productgrid', i, productgrids[i]);
        var influencerData = null;
        var influencerAttr = productgrids[i].getAttribute('data-influencer');
        if (influencerAttr) {
            try { influencerData = JSON.parse(influencerAttr); } catch (e) {}
        }
        loadProductGrid(productgrids[i], { influencer: influencerData });
    }

    // Dispatch firstload event after initial load
    var firstEvent;
    if (typeof CustomEvent === 'function') {
        firstEvent = new CustomEvent('firstload', { bubbles: true });
    } else {
        firstEvent = document.createEvent('CustomEvent');
        firstEvent.initCustomEvent('firstload', true, true, {});
    }
    document.dispatchEvent(firstEvent);
}

/**
 * Load pool - Load a specific productgrid (for tab switching)
 * Compatible with int-vue-productpool API
 */
function loadpool(eventLike) {
    var target = eventLike && eventLike.currentTarget;
    if (!target) return;

    var poolUrl = target.getAttribute('data-pool');
    if (!poolUrl) return;

    // Get influencer data if present
    var influencerAttr = target.getAttribute('data-influencer');
    var influencerData = null;
    if (influencerAttr) {
        try { influencerData = JSON.parse(influencerAttr); } catch (e) {}
    }

    // Find the associated productgrid
    var productpool = target.closest('.productpool');
    var productgrid = productpool ? productpool.querySelector('.productgrid') : null;

    if (productgrid) {
        productgrid.setAttribute('data-pool', poolUrl);
        if (influencerData) {
            productgrid.setAttribute('data-influencer', influencerAttr);
        } else {
            productgrid.removeAttribute('data-influencer');
        }
        loadProductGrid(productgrid, { influencer: influencerData });
    }
}

// Export as window.INTPRODUCTPOOL for compatibility with existing code
window.INTPRODUCTPOOL = {
    start: start,
    loadpool: loadpool
};

// Also export for CommonJS/module usage
module.exports = {
    start: start,
    loadpool: loadpool,
    fetchProducts: fetchProducts,
    extractProducts: extractProducts,
    renderProducts: renderProducts
};
